/* Revisie vastleggen, met of zonder PDF in de projectmap.
 *
 * Met PDF: de omschrijving is verplicht (ze wordt de Omschrijving van de Versie in de
 * projectmap en de notitie van de revisie). Is de huidige revisie al bevroren maar nog
 * zonder bestand, dan kun je kiezen: de PDF bij die revisie, of een nieuwe revisie. */

import { useEffect, useMemo, useState } from 'react';
import { FileText, FolderUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { kiesBestand, MIN_OMSCHRIJVING, type OfferteProject } from '../data/projectmap';
import { nummerVoorRevisie, type Revisie, type RevisieKeuze } from '../data/revisies';
import { datum } from '../../projectpagina/ui/opmaak';

export interface RevisieBevestiging {
  doel: { soort: 'bestaand'; revisie: Revisie } | { soort: 'nieuw'; nummer: number };
  omschrijving: string;
}

export function RevisieDialoog({
  open, onOpenChange, metPdf, keuze, quote, project, vorige, magNieuweVersie, bezig, onBevestig,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  /** true: PDF naar de projectmap; false: alleen de revisie vastleggen. */
  metPdf: boolean;
  keuze: RevisieKeuze;
  quote: { quote_number: string; current_version: number | null };
  project: OfferteProject | null;
  vorige: Pick<Revisie, 'file_name' | 'file_folder'> | null;
  /** Een volgende versie van een bestand zetten mag alleen een admin (ADR 0017). */
  magNieuweVersie: boolean;
  bezig: boolean;
  onBevestig: (b: RevisieBevestiging) => void;
}) {
  const [soort, setSoort] = useState<'bestaand' | 'nieuw'>(keuze.standaard);
  const [omschrijving, setOmschrijving] = useState('');
  useEffect(() => {
    if (open) {
      setSoort(metPdf ? keuze.standaard : 'nieuw');
      setOmschrijving('');
    }
  }, [open, metPdf, keuze.standaard]);

  const kanBestaand = metPdf && keuze.bestaand !== null;
  const nummer = soort === 'bestaand' && keuze.bestaand ? keuze.bestaand.version_number : keuze.nieuw;
  const nieuwNummer = soort === 'nieuw' && !keuze.nieuwIsHuidige ? nummerVoorRevisie(quote.quote_number, quote.current_version, nummer) : quote.quote_number;

  const naam = useMemo(() => {
    if (!metPdf || !project) return null;
    try {
      return kiesBestand({
        project,
        quoteNumber: nieuwNummer,
        currentVersion: soort === 'nieuw' ? nummer : quote.current_version,
        revisie: nummer,
        vorige,
        bestanden: [],
      }).naam;
    } catch (e) {
      return null;
    }
  }, [metPdf, project, nieuwNummer, soort, nummer, quote.current_version, vorige]);

  const tekst = omschrijving.trim();
  const geblokkeerd = metPdf && vorige && !magNieuweVersie;
  const geldig = metPdf ? tekst.length >= MIN_OMSCHRIJVING : true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {metPdf ? <FolderUp className="h-5 w-5" aria-hidden /> : <FileText className="h-5 w-5" aria-hidden />}
            {metPdf ? 'Opslaan in projectmap' : 'Revisie vastleggen'}
          </DialogTitle>
          <DialogDescription>
            {metPdf
              ? 'De PDF van de offerte zoals hij nu is, als nieuwe versie in de projectmap. Een bestaande versie wordt nooit overschreven.'
              : 'Bevriest de regels, bedragen en tekst van nu als revisie, zonder PDF.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!geldig || geblokkeerd) return;
            onBevestig({
              doel: soort === 'bestaand' && keuze.bestaand ? { soort: 'bestaand', revisie: keuze.bestaand } : { soort: 'nieuw', nummer: keuze.nieuw },
              omschrijving: tekst,
            });
          }}
        >
          {kanBestaand ? (
            <RadioGroup value={soort} onValueChange={(v) => setSoort(v as 'bestaand' | 'nieuw')} className="gap-2">
              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-sm has-[:checked]:border-foreground">
                <RadioGroupItem value="bestaand" className="mt-0.5" />
                <span>
                  <span className="font-medium">Bij revisie {keuze.bestaand!.version_number}</span>
                  <span className="block text-xs text-muted-foreground">
                    Vastgelegd op {datum(keuze.bestaand!.created_at)}, nog zonder PDF. De PDF toont wat nu in de offerte staat.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-sm has-[:checked]:border-foreground">
                <RadioGroupItem value="nieuw" className="mt-0.5" />
                <span>
                  <span className="font-medium">Nieuwe revisie {keuze.nieuw}</span>
                  <span className="block text-xs text-muted-foreground">Het offertenummer wordt {nummerVoorRevisie(quote.quote_number, quote.current_version, keuze.nieuw)}.</span>
                </span>
              </label>
            </RadioGroup>
          ) : (
            <p className="rounded-lg bg-muted/60 px-3 py-2 text-sm">
              Wordt <span className="font-medium">revisie {keuze.nieuw}</span>
              {!keuze.nieuwIsHuidige && nieuwNummer !== quote.quote_number && <> · nummer {nieuwNummer}</>}
            </p>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="revisie-omschrijving">{metPdf ? 'Omschrijving' : 'Notitie (optioneel)'}</Label>
            <Textarea
              id="revisie-omschrijving"
              rows={3}
              autoFocus
              value={omschrijving}
              onChange={(e) => setOmschrijving(e.target.value)}
              placeholder={metPdf ? 'Wat is deze versie, of wat is er veranderd? Bijvoorbeeld: zes batterijen, korting € 10.000 per stuk.' : 'Wat is er veranderd?'}
            />
            {metPdf && tekst.length > 0 && tekst.length < MIN_OMSCHRIJVING && (
              <span className="text-xs text-muted-foreground">Nog {MIN_OMSCHRIJVING - tekst.length} tekens.</span>
            )}
          </div>

          {metPdf && naam && (
            <div className="rounded-lg border px-3 py-2 text-xs">
              <span className="text-muted-foreground">Bestand in de projectmap{vorige?.file_folder ? ` (${vorige.file_folder})` : ''}</span>
              <div className="mt-0.5 break-all font-mono text-[12px]">{naam}</div>
              {vorige?.file_name && <div className="mt-0.5 text-muted-foreground">Nieuwe versie van {vorige.file_name}</div>}
            </div>
          )}
          {geblokkeerd && (
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Een nieuwe versie van een bestand in de projectmap zetten mag alleen een admin. Vraag een admin, of download de PDF.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={bezig || !geldig || Boolean(geblokkeerd)}>
              {bezig ? (metPdf ? 'PDF maken en uploaden…' : 'Bezig…') : metPdf ? 'Opslaan in projectmap' : 'Vastleggen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
